import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { quantityUnitActions } from "../slices/quantityUnit";
import { getErrorMessage } from "../../utils/errorUtils";
import { QuantityUnitRequestDto } from "../../shared/dtos/quantityUnit/QuantityUnitRequestDto";
import { QuantityUnitResponseDto } from "../../shared/dtos/quantityUnit/QuantityUnitResponseDto";
import { UpdateQuantityUnitPayload } from "../../shared/interfaces/quantityUnit/UpdateQuantityUnitPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getQuantityUnits =
  (params?: PaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quantityUnitActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<QuantityUnitResponseDto>>
      >("/quantity-units", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(quantityUnitActions.setItems(data.data.content));
        dispatch(quantityUnitActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(quantityUnitActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const createQuantityUnit =
  (values: QuantityUnitRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quantityUnitActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<QuantityUnitResponseDto>
      >("/quantity-units", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Unit created!");
        dispatch(quantityUnitActions.createSuccess(data.data));
        dispatch(
          quantityUnitActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      dispatch(quantityUnitActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const updateQuantityUnit =
  (payload: UpdateQuantityUnitPayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quantityUnitActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<QuantityUnitResponseDto>
      >(`/quantity-units/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Unit updated!");
        dispatch(quantityUnitActions.updateSuccess(data.data));
      }
    } catch (error) {
      dispatch(quantityUnitActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const deleteQuantityUnit =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quantityUnitActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/quantity-units/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Unit deleted!");
        dispatch(quantityUnitActions.deleteSuccess(id));
      }
    } catch (error) {
      dispatch(quantityUnitActions.setErrorDelete(getErrorMessage(error)));
    }
  };

export const resetQuantityUnitServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(quantityUnitActions.setServerStatus(null));
  };
